import React, {useContext, useMemo, useState} from "react";
import classnames from "classnames";
import {CircularProgress, Fade, SvgIcon} from "@mui/material";
import {ReactComponent as HorizontalDragIcon} from "../../../../../../../../../assets/images/notifications/horizontal-drag.svg";
import {deepEqual, preventEventPropagation} from "../../../../../../../../../core/services/utils/utils";
import SaveOrRollback from "../../../../../../../../components/app-specific/save-or-rollback";
import IconButton from "../../../../../../../../components/base/button/icon_button";
import {CalendarToday} from "@mui/icons-material";
import {ReactComponent as RemoveIcon} from "../../../../../../../../../assets/images/remove.svg";
import {UserHomepageComponentExpandability, UserHomepageComponentTypes, UserHomepageEventEmitter} from "../../../../../../models";
import {UserHomepageControllerContext, UserHomepageStateContext} from "../../../../../../index";
import useIsMounted from "../../../../../../../../hooks/use-is-mounted";
import UserHomepageUtils from "../../../../../../core/utils";

const UserHomepageControlPanelComponentHeader = ({
                                                     sortable,
                                                     component,
                                                     tabId,
                                                     update,
                                                     remove,
                                                     inEditMode,
                                                     onRevert,
                                                     currentComponent
                                                 }) => {
    const controller = useContext(UserHomepageControllerContext);
    const state = useContext(UserHomepageStateContext);
    const [updating, setUpdating] = useState(false);
    const [removing, setRemoving] = useState(false);
    const isMounted = useIsMounted();

    const disabled = useMemo(() => updating || removing, [updating, removing]);
    const showDatesIcon = useMemo(() => ![UserHomepageComponentTypes.timeBased].includes(component?.type?.id), [component?.type])
    const contentLength = useMemo(() => UserHomepageUtils.getComponentContentList(component).length, [component])
    const contentList = useMemo(() => UserHomepageUtils.getComponentContentList(component), [component])
    const currentContentList = useMemo(() => UserHomepageUtils.getComponentContentList(currentComponent), [currentComponent])

    const componentChanged = useMemo(() => {
        const normalizeForComparison = (comp, list) => ({
            ...comp,
            title: undefined,
            expanded: undefined,
            orderIndex: undefined,
            type: undefined,
            webViewElementId: undefined,
            phoneViewElementId: undefined,
            controlPanelElementId: undefined,
            content: undefined,
            chosen: undefined,
            selected: undefined,
            filtered: undefined,
            endDate: comp?.type?.id === UserHomepageComponentTypes.timeBased
                ? UserHomepageUtils.getTimeBasedFinishDateTime(comp) ?? undefined
                : comp?.endDate ?? undefined,
            contentList: list?.map(e => ({
                ...e,
                chosen: undefined,
                selected: undefined,
                filtered: undefined,
            })),
            imageWidth: comp?.content?.imageWidth ?? undefined,
            imageHeight: comp?.content?.imageHeight ?? undefined,
        });

        return !deepEqual(
            normalizeForComparison(component, contentList),
            normalizeForComparison(currentComponent, currentContentList),
        );
    }, [component, currentComponent, contentList, currentContentList]);

    const showSaveOrRollBack = useMemo(() =>
            componentChanged ||
            [
                UserHomepageComponentExpandability.dates
            ].includes(component.expanded),
        [componentChanged, component.expanded])

    /**
     * Updates the content of the current component in the server.
     *
     * * if the response is successful, commits the update changes.
     * @return {Promise<void>}
     */
    const updateComponent = async () => {
        setUpdating(true);
        const resultFlag = await update();
        if (!isMounted())
            return;
        setUpdating(false);
        if (resultFlag) {
            controller.updateComponent(tabId, component, true);
        }
    }

    /**
     * Removes the current component from the server.
     *
     * * if the result of the callback is successful, removes the component from the controller as well.
     * @return {Promise<void>}
     */
    const removeComponent = async () => {
        setRemoving(true);
        const resultFlag = await remove();
        if (!isMounted())
            return;
        setRemoving(false);
        if (resultFlag) {
            controller.removeComponent(tabId, component.id, state);
        }
    }

    /**
     * Reverts all the changes made to the component apart from the title of the component.
     */
    const revertComponentChanges = () => {
        if (!componentChanged) {
            return controller.openComponent(tabId, component.id, UserHomepageComponentExpandability.content);
        }
        controller.updateComponent(tabId, {
            ...currentComponent,
            title: component.title,
        }, true);
        if (onRevert) onRevert();
    }

    /**
     * Gets users confirmation before removing the current component entirely.
     */
    const getRemoveConfirmation = () => {
        window.partner?.openConfirmationDialog(
            <div className={'p-3'}>
                <p className={'text-sm text-color-2'}>
                    Are you sure that you want to remove this component? this action is irreversible.
                </p>
            </div>,
            {
                maxWidth: 'sm'
            },
            (v) => v && removeComponent(),
        );
    }

    /**
     * Opens the date section of the current component.
     * * selects the component in the view as well.
     */
    const openDatesSection = () => {
        controller.selectComponent(
            tabId,
            component.id,
            UserHomepageEventEmitter.controlPanel,
            UserHomepageComponentExpandability.dates,
        );
    }

    return (
        <>
            <div
                className={classnames('component-header', {'changed': showSaveOrRollBack})}
                // onClick={preventEventPropagation}
            >
                <Fade in={sortable} className={'mx-1 handle'}>
                    <SvgIcon component={HorizontalDragIcon}/>
                </Fade>
                <div className={'d-flex align-items-center justify-content-end flex-grow-1'}>
                    {
                        showSaveOrRollBack &&
                        <div
                            className={'d-flex align-items-center justify-content-end mr-2'}
                            onClick={preventEventPropagation}
                        >
                            <SaveOrRollback
                                show
                                loading={updating}
                                column={false}
                                onRollback={() => revertComponentChanges()}
                                onSave={() => updateComponent()}
                            />
                        </div>
                    }
                    {
                        !showSaveOrRollBack &&
                        <Fade in={!inEditMode}>
                            <div className={'d-flex align-items-center justify-content-end'}>
                                {
                                    showDatesIcon &&
                                    <IconButton
                                        disabled={disabled}
                                        className={"mx-1 ml-2 p-1"}
                                        color={'paper.secondary'}
                                        onPressed={(e) => preventEventPropagation(e, openDatesSection)}
                                    >
                                        <CalendarToday className={"icon small m-1"}/>
                                    </IconButton>
                                }
                                <Fade in={!inEditMode}>
                                    <IconButton
                                        onPressed={(e) => preventEventPropagation(e, getRemoveConfirmation)}
                                        disabled={disabled}
                                        color={'paper.error'}
                                    >
                                        {
                                            removing
                                                ? <CircularProgress size={15}/>
                                                : <SvgIcon component={RemoveIcon} className='icon normal'/>
                                        }
                                    </IconButton>
                                </Fade>
                            </div>
                        </Fade>
                    }
                    <div className={'count-container'}>
                        <p>
                            {contentLength}
                        </p>
                    </div>
                </div>
            </div>
        </>
    )
}

export default UserHomepageControlPanelComponentHeader;
